import type { CityConfig } from "@/pages/city-template/types";

export const wilsonvilleConfig: CityConfig = {
  city: "Wilsonville",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "wilsonville",
  route: "/cities/wilsonville",
  title: "Emergency Passport Wilsonville, OR | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Wilsonville, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Wilsonville tech corridor and south Portland metro travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Wilsonville, Oregon",
  heroSubtitle: "Business trip to Tokyo? Family vacation to Costa Rica? Passport expired before your departure from PDX? Fast Passport Center helps Wilsonville and south Portland metro travelers get passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Wilsonville%20Oregon%20modern%20tech%20business%20park%20with%20clean%20corporate%20buildings%20surrounded%20by%20lush%20Pacific%20Northwest%20greenery%2C%20Willamette%20River%20visible%20in%20distance%2C%20well-maintained%20suburban%20landscape%20with%20green%20open%20spaces%2C%20crisp%20blue%20sky%20with%20soft%20clouds%2C%20photorealistic%20corporate%20landscape%20photography%2C%20no%20text%20or%20signage%20visible&width=1800&height=700&seq=wilsonville-or-hero-01&orientation=landscape",
  heroImageAlt: "Wilsonville Oregon modern tech corridor with Pacific Northwest greenery and Willamette River",
  geoPosition: "45.2985;-122.7737",
  geoPlacename: "Wilsonville, Oregon",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "300 5th Ave, Suite 600, Seattle, WA 98104",
    distance: "About 175 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Wilsonville", zips: "97070" },
    { name: "Tualatin", zips: "97062" },
    { name: "Canby", zips: "97013" },
    { name: "Newberg", zips: "97132" },
    { name: "Sherwood", zips: "97140" },
    { name: "Tigard", zips: "97223, 97224" },
    { name: "Lake Oswego", zips: "97034–97035" },
    { name: "West Linn", zips: "97068" },
    { name: "Oregon City", zips: "97045" },
    { name: "Beaverton", zips: "97003–97008" },
    { name: "Portland", zips: "97201–97299" },
    { name: "Woodburn", zips: "97071" },
    { name: "Aurora", zips: "97002" },
    { name: "Donald", zips: "97020" },
    { name: "Hubbard", zips: "97032" },
    { name: "Stafford", zips: "97068" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required. Complete your order from your Wilsonville office or home in minutes — built for busy professionals." },
    { icon: "ri-truck-line", title: "Ship Overnight from Wilsonville", desc: "FedEx and UPS locations in Wilsonville and nearby Tualatin offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — important for tech professionals with last-minute international business trips." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no hunting for public appointments." },
  ],
  testimonials: [
    {
      name: "Marcus T.",
      location: "Wilsonville, OR",
      rating: 5,
      text: "Product launch in Tokyo announced with 10 days notice. My passport expired 3 months ago. Fast Passport Center got me a renewed passport in 4 days. I shipped from the Tualatin FedEx at 7pm and had it in time. Tech corridor life — you need this service.",
      service: "Passport Renewal",
    },
    {
      name: "Diana K.",
      location: "Wilsonville, OR",
      rating: 5,
      text: "Family vacation to Costa Rica — discovered my husband's passport had only 3 months validity at check-in. We rescheduled. Fast Passport Center handled the renewal in 6 days so we didn't miss our rebooked trip. Amazing service.",
      service: "Passport Renewal",
    },
    {
      name: "Kevin H.",
      location: "Tualatin, OR",
      rating: 5,
      text: "Lost my passport in Portland two weeks before a conference in London. Fast Passport Center walked me through the DS-64, replacement process, and had me a new passport in 7 days. Unbelievably smooth.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "wilsonville-post",
      name: "Wilsonville Post Office",
      type: "USPS Passport Acceptance",
      address: "29020 SW Town Center Loop E",
      city: "Wilsonville",
      state: "OR",
      zip: "97070",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Wilsonville Town Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Wilsonville. Appointment required — book online in advance. Photo services may be available.",
      icon: "ri-mail-line",
    },
    {
      id: "tualatin-post",
      name: "Tualatin Post Office",
      type: "USPS Passport Acceptance",
      address: "19250 SW Martinazzi Ave",
      city: "Tualatin",
      state: "OR",
      zip: "97062",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Tualatin — about 4 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Tualatin option for north Wilsonville and Stafford area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "sherwood-post",
      name: "Sherwood Post Office",
      type: "USPS Passport Acceptance",
      address: "14895 SW Sherwood Blvd",
      city: "Sherwood",
      state: "OR",
      zip: "97140",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Sherwood — about 6 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Sherwood option for northwest Wilsonville residents and commuters.",
      icon: "ri-mail-line",
    },
    {
      id: "canby-post",
      name: "Canby Post Office",
      type: "USPS Passport Acceptance",
      address: "175 N Grant St",
      city: "Canby",
      state: "OR",
      zip: "97013",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Canby — about 8 miles southeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Canby option for southeast Wilsonville and Aurora area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "lake-oswego-post",
      name: "Lake Oswego Post Office",
      type: "USPS Passport Acceptance",
      address: "4000 Kruse Way Pl",
      city: "Lake Oswego",
      state: "OR",
      zip: "97035",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Lake Oswego — about 8 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Lake Oswego option for north Wilsonville and affluent Portland suburb residents.",
      icon: "ri-mail-line",
    },
    {
      id: "west-linn-post",
      name: "West Linn Post Office",
      type: "USPS Passport Acceptance",
      address: "4380 Willamette Falls Dr",
      city: "West Linn",
      state: "OR",
      zip: "97068",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "West Linn — about 9 miles northeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "West Linn option for northeast Wilsonville and Clackamas County residents.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Wilsonville before an international business trip?",
      a: "Yes. Through Fast Passport Center, Wilsonville business travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours. Wilsonville's tech corridor generates a lot of last-minute international travel — we handle it regularly. Start your order online today.",
    },
    {
      q: "How fast can I renew my passport in Wilsonville?",
      a: "Fast passport renewal in Wilsonville is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my business trip is confirmed with less than 2 weeks notice?",
      a: "This is one of the most common situations we handle for Wilsonville tech professionals. If your international trip is confirmed with less than 2 weeks notice, start your order with Fast Passport Center immediately. Depending on your travel date, you may qualify for our 2–9 business day expedited service or our emergency 24-hour processing.",
    },
    {
      q: "Can I get a same-day passport appointment near Wilsonville?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Wilsonville, Fast Passport Center is the faster and more predictable option — no three-hour Seattle drive needed.",
    },
    {
      q: "Can I expedite a child passport in Wilsonville?",
      a: "Yes. Child passport expediting in Wilsonville is possible through Fast Passport Center, but child passports (for minors 15 and under) require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen, act immediately. You will need to complete Form DS-64 to report it and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps.",
    },
    {
      q: "Does Wilsonville have a passport agency?",
      a: "Wilsonville, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 175 miles north) and the San Francisco Passport Agency in California (about 650 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster, more convenient solution.",
    },
    {
      q: "How long does a standard passport take in Wilsonville?",
      a: "Standard USPS passport processing from Wilsonville runs 6–13 weeks — sometimes longer during peak travel season. If your trip is sooner, you need expedited processing. Fast Passport Center offers Wilsonville travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I renew my passport in Wilsonville without visiting a passport office?",
      a: "Yes. Most Wilsonville adults renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home or office, and ship them overnight from any FedEx or UPS location in Wilsonville or Tualatin.",
    },
    {
      q: "What airports do Wilsonville travelers fly from?",
      a: "Wilsonville travelers primarily use Portland International Airport (PDX) — about 20 miles northeast — for all domestic and international departures. PDX offers direct flights to Amsterdam, London, Tokyo, Vancouver, and more. Some travelers use Seattle-Tacoma (SEA) for additional international options. Fast Passport Center can help you meet your deadline.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Wilsonville", address: "29040 SW Town Center Loop E, Wilsonville, OR 97070", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Wilsonville", address: "30000 SW Boones Ferry Rd, Wilsonville, OR 97070", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Tualatin", address: "19015 SW Tualatin Valley Hwy, Tualatin, OR 97062", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM" },
    { name: "The UPS Store — Tualatin", address: "19238 SW Martinazzi Ave, Tualatin, OR 97062", hours: "Mon–Fri 8 AM – 7 PM" },
    { name: "Walgreens — Tualatin", address: "19405 SW Tualatin Valley Hwy, Tualatin, OR 97062", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Wilsonville", address: "8500 SW Boeckman Rd, Wilsonville, OR 97070", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Sherwood", address: "16050 SW Langer Dr, Sherwood, OR 97140", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Lake Oswego", address: "525 SW Mcvey Ave, Lake Oswego, OR 97034", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Lake Oswego", address: "1500 NW Bethany Blvd, Lake Oswego, OR 97034", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "Walgreens — Canby", address: "1400 N Holly St, Canby, OR 97013", hours: "Daily 8 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Wilsonville DMV Office",
      address: "29020 SW Town Center Loop E, Wilsonville, OR 97070",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Wilsonville DMV office. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Tualatin DMV Office",
      address: "8800 SW Commercial St, Tualatin, OR 97062",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Tualatin option for north Wilsonville residents and tech corridor commuters.",
      featured: false,
    },
    {
      name: "Oregon City DMV Office",
      address: "1021 S Molalla Ave, Oregon City, OR 97045",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Clackamas County option for east Wilsonville and Canby area residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 20 miles northeast",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX offers direct international flights to Amsterdam, London, Tokyo, Vancouver, and more. Wilsonville's tech professionals are frequent PDX flyers for international business travel.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 175 miles north",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Wilsonville travelers use SEA for additional international options. Passport required for all international departures.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Last-minute international business trip from Wilsonville? Our emergency courier service handles urgent passport needs for tech professionals and corporate travelers — as fast as 24 hours.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Wilsonville residents and tech corridor professionals renew fast — perfect for business trips to Asia, Europe, and Latin America.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Wilsonville", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Wilsonville travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Wilsonville families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Wilsonville residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, bypassing the public queue. Built for busy Wilsonville professionals.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent international travelers — all handled with expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Wilsonville, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Wilsonville residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Wilsonville, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Wilsonville travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Wilsonville families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Wilsonville, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Not Checking Passport Validity Before Booking", description: "Wilsonville tech professionals often book international trips through corporate travel and don't personally verify passport validity. Many countries require 6 months validity beyond return date. Check before you book." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and office printers often get rejected for lighting, background, or expression issues. We provide exact specifications and review photos before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application is returned and you lose weeks. Our pre-submission checklist catches errors before they become expensive delays for your business travel." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your specific situation." },
    { icon: "ri-calendar-close-line", title: "Assuming Your Corporate Travel Team Can Handle It", description: "Corporate travel agencies book flights and hotels — they cannot expedite passports. Fast Passport Center is the dedicated passport expediting expert. Start the moment your international trip is confirmed." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Between Passport and Airline Ticket", description: "Your passport name must match your airline ticket exactly — especially for international flights out of PDX. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Wilsonville travel date. Complete your secure order online — takes just a few minutes, even from your office." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Wilsonville", description: "Drop off at any FedEx or UPS in the Wilsonville or Tualatin area — most locations are open evenings. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping — you travel on time." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3 hours each way)", fpc: "Ship documents overnight from Wilsonville" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/cities/portland" },
    { name: "Fast passport renewal in Tualatin", route: "/cities/tualatin" },
    { name: "Emergency passport help near Lake Oswego", route: "/cities/lake-oswego" },
    { name: "Rush passport service in West Linn", route: "/cities/west-linn" },
    { name: "Fast passport renewal for south Portland metro travelers", route: "/states/oregon" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Wilsonville or Tualatin — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Wilsonville and all of the south Portland metro remotely",
  serviceAreaIntro: "Whether you are in Wilsonville's tech corridor, Stafford, Aurora, or anywhere in the south Portland metro, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Wilsonville and south metro zip codes. Anywhere in Clackamas, Washington, or Yamhill County — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Wilsonville as simple as possible. Most Wilsonville clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Wilsonville tech professionals and families. We review your documents before submission to catch issues before they become problems.",
  testimonialsIntro: "Real reviews from Wilsonville, Tualatin, and south Portland metro travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Wilsonville and south Portland metro travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
};